import type { Pattern } from '../../contexts/PatternContext'
import { PatternDraftDiff } from '../types'

type Indexed = { conditions: Map<string, string>; groups: Set<string> }

// Key-order-stable serialization: the editor object and the rebuilt agent
// draft can order keys differently, and raw JSON.stringify would report an
// echoed-but-unchanged condition as modified
const stableStringify = (value: any): string => {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null'
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`
  const keys = Object.keys(value)
    .filter((key) => value[key] !== undefined)
    .sort()
  return `{${keys.map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`
}

const indexPattern = (pattern: Pattern): Indexed => {
  const conditions = new Map<string, string>()
  const groups = new Set<string>()
  const walk = (group: any) => {
    if (!group) return
    groups.add(group.id)
    for (const condition of group.conditions ?? []) {
      conditions.set(condition.id, stableStringify(condition))
    }
    ;(group.conditionGroups ?? []).forEach(walk)
  }
  ;(pattern.buyConditionGroups ?? []).forEach(walk)
  ;(pattern.sellConditionGroups ?? []).forEach(walk)
  return { conditions, groups }
}

const countPhrase = (n: number, noun: string): string | null =>
  n > 0 ? `${n} ${noun}${n === 1 ? '' : 's'}` : null

/** Id-level structural diff between two pattern drafts, for the DraftCard
 * summary and flash highlights. Echoed ids make this exact; regenerated ids
 * degrade gracefully to added+removed. */
export const diffPatternDrafts = (before: Pattern, after: Pattern): PatternDraftDiff => {
  const a = indexPattern(before)
  const b = indexPattern(after)

  const addedConditionIds: string[] = []
  const modifiedConditionIds: string[] = []
  const removedConditionIds: string[] = []

  b.conditions.forEach((json, id) => {
    if (!a.conditions.has(id)) addedConditionIds.push(id)
    else if (a.conditions.get(id) !== json) modifiedConditionIds.push(id)
  })
  a.conditions.forEach((_json, id) => {
    if (!b.conditions.has(id)) removedConditionIds.push(id)
  })

  const addedGroupIds = [...b.groups].filter((id) => !a.groups.has(id))
  const removedGroupIds = [...a.groups].filter((id) => !b.groups.has(id))

  const summaryParts = [
    addedConditionIds.length > 0 ? `${addedConditionIds.length} added` : null,
    modifiedConditionIds.length > 0 ? `${modifiedConditionIds.length} changed` : null,
    removedConditionIds.length > 0 ? `${removedConditionIds.length} removed` : null,
    countPhrase(addedGroupIds.length, 'new group'),
  ].filter(Boolean)

  return {
    addedConditionIds,
    modifiedConditionIds,
    removedConditionIds,
    addedGroupIds,
    removedGroupIds,
    summary:
      summaryParts.length > 0 ? `Conditions: ${summaryParts.join(', ')}` : 'No structural changes',
  }
}
